import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { ILayout, useLayout } from '../../core'
import { SidebarMenu } from './sidebar-menu/SidebarMenu'
import { SidebarFooter } from './SidebarFooter'
import SidebarLogo from './SidebarLogo'
import { useThemeMode } from '../../../partials'
import { useAuth } from '../../../../app/modules/auth'
import { getSuccessGuide } from '../../../../app/modules/dashboard/core/_requests'

const Sidebar = () => {
  const { config } = useLayout()
  const { mode } = useThemeMode()
  const [, setLoading] = useState(false)
  const { companyId } = useAuth()
  const [successGuidePercentage, setSuccessGuidePercentage] = useState<number>(0)
  const getSuccessGuideDetails = async () => {
    if (process.env.REACT_APP_BRAND === 'RK') {
      try {
        setLoading(true)
        const {
          data: { success, data },
        } = await getSuccessGuide(+companyId!)
        if (success) {
          const fields = [
            data.successGuides.storage,
            data.successGuides.companyDetails,
            data.successGuides.teamMember,
            data.successGuides.rkChats,
            data.successGuides.categorizedFiles,
            data.successGuides.aiInsights,
          ]

          // Count how many fields are greater than 0
          const completedSteps = fields.filter((value) => value > 0).length

          // Calculate percentage
          const percentage = (completedSteps / 6) * 100

          setSuccessGuidePercentage(percentage)

          setLoading(false)
        } else {
          setLoading(false)
        }
      } catch (err) {
        setLoading(false)
        console.log(err)
      }
    }
  }

  useEffect(() => {
    if (companyId) {
      getSuccessGuideDetails()
    }
  }, [companyId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    updateDOM(config)
  }, [config])

  if (!config.app?.sidebar?.display) {
    return null
  }

  return (
    <div id='kt_app_sidebar' className={clsx('app-sidebar', config.app?.sidebar?.default?.class)}>
      <div className='d-flex flex-column justify-content-start h-100'>
        <SidebarLogo />
        <div className='d-flex flex-column flex-grow-1 '>
          <div
            style={{
              overflowY: 'scroll',
              flex: '1 1 auto', // Flex-grow and flex-shrink behavior for SidebarMenu
              maxHeight: successGuidePercentage === 100 ? 'calc(100vh - 170px)' : 'calc(100vh - 70px - 170px)', // Adjust height of SidebarMenu dynamically based on footer and logo heights
            }}
          >
            <SidebarMenu />
          </div>
          <div
            style={{
              position: 'sticky',
              bottom: 0,
              zIndex: 1, // Ensures it stays above other content if necessary
              background: mode === 'dark' ? '#1e1e2d' : '#ffffff'
            }}
          >
            <SidebarFooter getSuccessGuideDetails={getSuccessGuideDetails}
              successGuidePercentage={successGuidePercentage} />
          </div>
        </div>
      </div>
    </div >
  )
}

const updateDOM = (config: ILayout) => {
  if (config.app?.sidebar?.default?.minimize?.desktop?.enabled) {
    if (config.app?.sidebar?.default?.minimize?.desktop?.default) {
      document.body.setAttribute('data-kt-app-sidebar-minimize', 'on')
    }

    if (config.app?.sidebar?.default?.minimize?.desktop?.hoverable) {
      document.body.setAttribute('data-kt-app-sidebar-hoverable', 'true')
    }
  }

  if (config.app?.sidebar?.default?.minimize?.mobile?.enabled) {
    if (config.app?.sidebar?.default?.minimize?.mobile?.default) {
      document.body.setAttribute('data-kt-app-sidebar-minimize-mobile', 'on')
    }

    if (config.app?.sidebar?.default?.minimize?.mobile?.hoverable) {
      document.body.setAttribute('data-kt-app-sidebar-hoverable-mobile', 'true')
    }
  }

  if (config.app?.sidebar?.default?.collapse?.desktop?.enabled) {
    if (config.app?.sidebar?.default?.collapse?.desktop?.default) {
      document.body.setAttribute('data-kt-app-sidebar-collapse', 'on')
    }
  }

  if (config.app?.sidebar?.default?.collapse?.mobile?.enabled) {
    if (config.app?.sidebar?.default?.collapse?.mobile?.default) {
      document.body.setAttribute('data-kt-app-sidebar-collapse-mobile', 'on')
    }
  }

  if (config.app?.sidebar?.default?.push) {
    if (config.app?.sidebar?.default?.push?.header) {
      document.body.setAttribute('data-kt-app-sidebar-push-header', 'true')
    }

    if (config.app?.sidebar?.default?.push?.toolbar) {
      document.body.setAttribute('data-kt-app-sidebar-push-toolbar', 'true')
    }

    if (config.app?.sidebar?.default?.push?.footer) {
      document.body.setAttribute('data-kt-app-sidebar-push-footer', 'true')
    }
  }

  if (config.app?.sidebar?.default?.stacked) {
    document.body.setAttribute('app-sidebar-stacked', 'true')
  }

  document.body.setAttribute('data-kt-app-sidebar-enabled', 'true')
  document.body.setAttribute(
    'data-kt-app-sidebar-fixed',
    config.app?.sidebar?.default?.fixed?.desktop?.toString() || ''
  )

  const appSidebarDefaultDrawerEnabled = config.app?.sidebar?.default?.drawer?.enabled
  let appSidebarDefaultDrawerAttributes: { [attrName: string]: string } = {}
  if (appSidebarDefaultDrawerEnabled) {
    appSidebarDefaultDrawerAttributes = config.app?.sidebar?.default?.drawer?.attributes as {
      [attrName: string]: string
    }
  }

  const appSidebarDefaultStickyEnabled = config.app?.sidebar?.default?.sticky?.enabled
  let appSidebarDefaultStickyAttributes: { [attrName: string]: string } = {}
  if (appSidebarDefaultStickyEnabled) {
    appSidebarDefaultStickyAttributes = config.app?.sidebar?.default?.sticky?.attributes as {
      [attrName: string]: string
    }
  }

  setTimeout(() => {
    const sidebarElement = document.getElementById('kt_app_sidebar')
    // sidebar
    if (sidebarElement) {
      const sidebarAttributes = sidebarElement
        .getAttributeNames()
        .filter((t) => t.indexOf('data-') > -1)
      sidebarAttributes.forEach((attr) => sidebarElement.removeAttribute(attr))

      if (appSidebarDefaultDrawerEnabled) {
        for (const key in appSidebarDefaultDrawerAttributes) {
          if (appSidebarDefaultDrawerAttributes.hasOwnProperty(key)) {
            sidebarElement.setAttribute(key, appSidebarDefaultDrawerAttributes[key])
          }
        }
      }

      if (appSidebarDefaultStickyEnabled) {
        for (const key in appSidebarDefaultStickyAttributes) {
          if (appSidebarDefaultStickyAttributes.hasOwnProperty(key)) {
            sidebarElement.setAttribute(key, appSidebarDefaultStickyAttributes[key])
          }
        }
      }
    }
  }, 0)
}

export { Sidebar }
